import { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCreateStaffMutation } from '@/store/api/apiSlice';
import { UserRole } from '@/types';
import type { StaffRole } from '@/types';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface CreateStaffModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ROLE_OPTIONS: { value: StaffRole; label: string }[] = [
    { value: UserRole.AGENT, label: 'Agent' },
    { value: UserRole.CUSTOMER_SUCCESS, label: 'Customer Success' },
    { value: UserRole.MEDICAL, label: 'Medical' },
    { value: UserRole.NOTARY, label: 'Notary' },
    { value: UserRole.POLICE, label: 'Police' },
    { value: UserRole.HR, label: 'HR Partner' },
];

const EMPTY_FORM = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: UserRole.AGENT as StaffRole,
    password: '',
};

export default function CreateStaffModal({ isOpen, onClose }: CreateStaffModalProps) {
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [createStaff, { isLoading }] = useCreateStaffMutation();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'phoneNumber') {
            setFormData((prev) => ({ ...prev, phoneNumber: value.replace(/[^\d+]/g, '') }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Normalise to E.164 (+234…). Accepts 080…, 80…, +234… and 234… shapes.
    const normalisePhone = (raw: string): string => {
        let digits = raw.replace(/\D/g, '');
        if (raw.startsWith('+')) return `+${digits}`;
        if (digits.startsWith('234')) return `+${digits}`;
        digits = digits.replace(/^0+/, '');
        return `+234${digits}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            setError('First and last name are required.');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }
        const phoneNumber = normalisePhone(formData.phoneNumber);
        if (phoneNumber.replace(/\D/g, '').length < 11) {
            setError('Please enter a valid phone number.');
            return;
        }
        if (formData.password && formData.password.length < 6) {
            setError('Password must be at least 6 characters (or leave it blank for OTP-only login).');
            return;
        }

        try {
            await createStaff({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phoneNumber,
                role: formData.role,
                ...(formData.password.trim() ? { password: formData.password.trim() } : {}),
            }).unwrap();
            setSuccess('Staff user created successfully.');
            setTimeout(() => {
                setFormData({ ...EMPTY_FORM });
                setSuccess('');
                onClose();
            }, 1500);
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to create staff user.'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="ml-auto h-full w-full md:max-w-md bg-white shadow-2xl z-10 flex flex-col animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-[#FF6B35]" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Add Staff</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Create an agent, CS, medical, notary or HR user
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Role */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Role <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                            >
                                {ROLE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                placeholder="Enter first name"
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                placeholder="Enter last name"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                placeholder="staff@example.com"
                            />
                        </div>

                        {/* Phone Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                placeholder="+2348012345678 or 08012345678"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                                Defaults to Nigeria (+234) when no code is given.
                            </p>
                        </div>

                        {/* Password (optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Password <span className="text-gray-400">(Optional)</span>
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                placeholder="Leave blank for OTP-only login"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">Minimum 6 characters if set.</p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                                {success}
                            </div>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="outline"
                            className="flex-1 py-2.5 rounded-lg border-gray-300 hover:bg-gray-100"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white py-2.5 rounded-lg font-medium"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="animate-spin h-4 w-4" />
                                    Creating...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <UserPlus className="w-4 h-4" />
                                    Create Staff
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
